import React, { useState, useEffect } from 'react';
import { createBooking, getUser } from '../../helpers/lib/axios';
import { getValidToken } from '../../helpers/lib/jwt';
import { useAppToast } from '../../helpers/lib/useAppToast'
import { PopupButton, useCalendlyEventListener } from 'react-calendly';

const CONTACT_EMAIL = 'contact@noohraservices.org';
const SAVE_ATTEMPTS = 3;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The Calendly slot is already booked when this runs, so retry transient failures before giving up.
async function createBookingWithRetry(payload) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await createBooking(payload);
    } catch (error) {
      if (attempt >= SAVE_ATTEMPTS) throw error;
      await wait(1500 * attempt);
    }
  }
}

export default function AppointmentForm() {
  const toast = useAppToast()
  const [name, setName] = useState('')
  const [age, setAge] = useState('')
  const [gender, setGender] = useState('')
  const [maritalStatus, setMaritalStatus] = useState('')
  const [cityOfResidence, setCityOfResidence] = useState('')
  const [complaint, setComplaint] = useState('')
  const [duration, setDuration] = useState('')
  const [expectations, setExpectations] = useState('')
  const [referral, setReferral] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false);
  // Set when Calendly booked a slot but saving the intake details failed; the user can retry.
  const [unsavedPayload, setUnsavedPayload] = useState(null);

  useEffect(() => {
    if (!getValidToken()) return;
    getUser().then(user => {
      const data = user?.data;
      const fullName = [data?.firstName, data?.lastName].filter(Boolean).join(' ');
      if (fullName) setName(fullName)
      if (data?.gender) setGender(data.gender)
      if (data?.email) setEmail(data.email)
    }).catch(() => { /* not signed in or profile unavailable: leave the form blank */ })
  }, [])

  const isComplete = Boolean(name && age && gender && maritalStatus && cityOfResidence && email && complaint && duration && expectations && referral);

  const saveIntake = async (payload) => {
    setLoading(true);
    try {
      await createBookingWithRetry(payload);
      setUnsavedPayload(null);
      resetFormFields();
      toast({
        status: "success",
        description: "Booking created successfully",
      });
    } catch (error) {
      setUnsavedPayload(payload);
      toast({
        status: "error",
        autoClose: false,
        description: `Your session time is booked, but we couldn't save your intake details. Please press "Retry saving my details", or email them to ${CONTACT_EMAIL}.`,
      });
    } finally {
      setLoading(false);
    }
  };

  useCalendlyEventListener({
    onEventScheduled: () => {
      if (!isComplete) {
        toast({
          status: 'error',
          description: 'Please fill all required fields',
        })
        return;
      }
      saveIntake({
        email,
        name,
        age,
        gender,
        maritalStatus,
        cityOfResidence,
        complaint,
        therapyExpectations: expectations,
        duration,
        channel: referral
      });
    },
  });

  const resetFormFields = () => {
    setName("");
    setAge("");
    setGender("");
    setMaritalStatus("");
    setCityOfResidence("");
    setComplaint("");
    setDuration("");
    setExpectations("");
    setReferral("");
  };

  const labelClass = "text-[0.72rem] tracking-[0.12em] uppercase text-charcoal/60 font-medium pb-2 block";
  const inputClass = "bg-cream border border-cream-dark rounded-sm py-[0.85rem] px-4 text-charcoal font-sans text-[0.9rem] font-light outline-none transition-colors w-full focus:border-crimson/60 focus:bg-white";

  return (
    <form onSubmit={(e) => e.preventDefault()} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">

      <div>
        <label className={labelClass}>Name</label>
        <input type="text" className={inputClass} placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <label className={labelClass}>Age</label>
        <input
          type="number"
          className={inputClass}
          placeholder="Enter age"
          value={age} onChange={(e) => setAge(e.target.value)}
        />
      </div>
      <div>
        <label className={labelClass}>Email</label>
        <input
          type="email"
          className={inputClass}
          placeholder="Enter email"
          value={email} onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div>
        <label className={labelClass}>
          Gender
        </label>
        <select name="" id="" className={inputClass} value={gender} onChange={(e) => setGender(e.target.value)}>
          <option value="" disabled>Select gender</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>
          Marital Status
        </label>
        <select name="" id="" className={inputClass} value={maritalStatus} onChange={(e) => setMaritalStatus(e.target.value)}>
          <option value="" disabled>Select marital status</option>
          <option value="Single">Single</option>
          <option value="Married">Married</option>
          <option value="Divorced">Divorced</option>
          <option value="Widowed">Widowed</option>
          <option value="Single Parent">Single Parent</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>City of residence</label>
        <input type="text" className={inputClass} placeholder="Enter city" value={cityOfResidence} onChange={(e) => setCityOfResidence(e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>What is your presenting complaint?</label>
        <textarea type="text" className={inputClass} placeholder="Enter text" value={complaint} onChange={(e) => setComplaint(e.target.value)} rows="3" />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>How long has this been going on?</label>
        <input type="text" className={inputClass} placeholder="Enter duration" value={duration} onChange={(e) => setDuration(e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>What are your expectations for therapy?</label>
        <textarea type="text" className={inputClass} placeholder="Enter text" value={expectations} onChange={(e) => setExpectations(e.target.value)} rows="3" />
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>
          How did you hear about us?
        </label>
        <select name="" id="" className={inputClass} value={referral} onChange={(e) => setReferral(e.target.value)}>
          <option value="" disabled>Select option</option>
          <option value="Instagram">Instagram</option>
          <option value="X (formerly known as Twitter)">X (formerly known as Twitter)</option>
          <option value="LinkedIn">LinkedIn</option>
          <option value="A friend on social media">A friend on social media</option>
          <option value="Word of mouth">Word of mouth</option>
        </select>
      </div>
      <div className="md:col-span-2 mt-4">
        {unsavedPayload ? (
          <button
            type="button"
            onClick={() => saveIntake(unsavedPayload)}
            disabled={loading}
            className="inline-block bg-crimson text-white px-9 py-3.5 text-xs font-medium tracking-[0.12em] uppercase rounded-[2px] transition-all duration-250 hover:bg-crimson-dark disabled:opacity-50"
          >
            {loading ? "Saving..." : "Retry saving my details"}
          </button>
        ) : isComplete ? (
          <PopupButton
            url="https://calendly.com/noohranigeria/30min"
            rootElement={document.getElementById("root")}
            text={loading ? "Saving..." : "Submit to Schedule"}
            textColor="#ffffff"
            color="#8B1A1A"
            className="inline-block bg-crimson text-white px-9 py-3.5 text-xs font-medium tracking-[0.12em] uppercase rounded-[2px] transition-all duration-250 hover:bg-crimson-dark hover:-translate-y-px hover:shadow-lg focus:outline-none"
          />
        ) : (
          <button
            type="button"
            className="inline-block bg-crimson/50 cursor-not-allowed text-white px-9 py-3.5 text-xs font-medium tracking-[0.12em] uppercase rounded-[2px]"
            disabled
          >
            Fill all fields
          </button>
        )}
      </div>
    </form>
  );
}
